const post = {
  "slug": "instagram-auto-reply-price-size-questions",
  "title": "Instagram Auto-Reply for Price and Size Questions",
  "seoTitle": "Auto-Reply to Price and Size Questions",
  "description": "Customers ask \u2019red kurti under 500?\u2019 or \u2019size M?\u2019 all day. See how the Checkout DM bot searches your catalog and answers, and how to write listings that get found.",
  "excerpt": "The bot can answer \u2019red kurti under 500\u2019 or \u2019do you have size M\u2019 straight from your catalog. Here is exactly what it matches, so your products are not missed.",
  "category": "Catalog",
  "tags": [
    "Instagram auto reply",
    "Answer price questions",
    "Instagram catalog search",
    "Size questions"
  ],
  "date": "2026-11-26",
  "cover": {
    "src": "/blog-images/instagram-auto-reply-price-size-questions-cover.png",
    "alt": "A customer typing red kurti under 500 and the bot returning matching products"
  },
  "blocks": [
    {
      "type": "p",
      "text": "Sellers answer the same few questions all day: how much is this, do you have it in my size, what do you have under 500? Checkout DM can answer these straight from your catalog. A customer types a question instead of tapping through a menu, and the bot searches your products before it falls back to showing everything. This guide explains exactly what it matches, so you know why a product did or did not appear and how to fix it."
    },
    {
      "type": "h2",
      "text": "What the bot actually searches"
    },
    {
      "type": "p",
      "text": "When a customer types something such as red kurti under 500 or do you have size M, the bot searches your product Title and Description, combined and matched without regard to capital letters. It searches nothing else: not the photo, not other fields. If a word is not in the Title or Description, the product will not show up for it, however obvious it is from the photo."
    },
    {
      "type": "ul",
      "items": [
        "**Words, not exact phrases.** The message is split into words. Filler such as a, the, looking for, do you have and please is dropped automatically, and every remaining word has to appear in the Title and Description. It is an AND, not an OR. I\u2019m looking for a red kurti please becomes a search for red and kurti.",
        "**Sizes are structured data.** A size only matches if it is an actual size on that product in your Sizes and stock fields. Writing available in M, L, XL inside the description does nothing for search.",
        "**Price ranges in plain language.** Under 500, below 1k, above 2000, 500 to 1000 and between 500 and 1000 are all understood, and price ranges apply fully on every plan.",
        "**Simple plurals match.** Shirts and shirt match the same products, as do dresses and dress."
      ]
    },
    {
      "type": "callout",
      "tone": "peach",
      "title": "The shirt and T-shirt rule",
      "text": "Searching shirt on its own deliberately leaves out products that only match because they contain T-Shirt, since tees are treated as a separate category. Tshirt, t-shirt and t shirt are all read as the same word and match your tees. If you sell both, name your tees explicitly T-Shirt so the two searches do not collide."
    },
    {
      "type": "h2",
      "text": "Write listings the way customers type"
    },
    {
      "type": "p",
      "text": "Because search only reads your Title and Description, how you write them decides what customers can find."
    },
    {
      "type": "ul",
      "items": [
        "**Say it the way a customer would.** A title such as Red Cotton Kurti, Straight Fit is found by red, cotton and kurti. A title such as Ethnic Wear 4 is found by none of them.",
        "**Cover alternate names.** Search matches literal words and does not know synonyms. If customers might type kurta where you say kurti, or hoodie where you say sweatshirt, mention both.",
        "**Keep it readable.** Your description appears in the bot\u2019s replies exactly as written, so one accurate sentence with the right words beats a list of keywords.",
        "**Put sizes in the Sizes field, always.** Typing them only in the description is the most common way size search silently breaks."
      ]
    },
    {
      "type": "p",
      "text": "Our guides to [writing product descriptions](/blog/write-product-descriptions-instagram-sellers) and [Instagram catalog tips](/blog/instagram-catalog-tips-photos-sizes) cover the writing side in more depth."
    },
    {
      "type": "h2",
      "text": "How many words it combines depends on your plan"
    },
    {
      "type": "p",
      "text": "The matching rules are the same on every plan. What changes is how many things, keyword words plus a size, the bot combines from one message. Free combines 1, Starter 2, Growth 3 and Pro 4. Price ranges are never limited by this. Keywords are taken in the order the customer typed them, so on lower plans the earlier words matter more. For red kurti size M:"
    },
    {
      "type": "table",
      "headers": [
        "Plan",
        "What is applied"
      ],
      "rows": [
        [
          "Free (1)",
          "Size M only. Red and kurti are dropped, so any product in size M matches."
        ],
        [
          "Starter (2)",
          "Size M and red. Kurti is dropped."
        ],
        [
          "Growth (3)",
          "Size M, red and kurti. The full search."
        ],
        [
          "Pro (4)",
          "The full search, with room for a longer one."
        ]
      ]
    },
    {
      "type": "p",
      "text": "On the Free plan this rarely matters in practice. With a cap of 5 products, most customers simply see your whole catalog and never need to search. Search becomes useful as your catalog grows."
    },
    {
      "type": "h2",
      "text": "Browsing by category, gender and colour"
    },
    {
      "type": "p",
      "text": "Three optional product fields help the bot filter more precisely. Category is free text such as Tops, Bottoms or Accessories, and powers a command that lets a customer browse by category. It is available from Creator upward. Gender (Men, Women, Unisex or Kids) and Color are available from Starter upward and combine with keyword search, so men black jeans filters by gender and colour at once."
    },
    {
      "type": "h2",
      "text": "Stock and sold-out items"
    },
    {
      "type": "p",
      "text": "Stock tracking is optional per product and per size. A blank quantity means the bot never worries about running out. A number, including zero, switches that product or size into tracked mode: the count drops with every order and the item is hidden from customers at zero. To bring it back, edit it and enter a quantity above zero. It becomes available again immediately, with no separate republish step."
    },
    {
      "type": "h2",
      "text": "A quick audit of your catalog"
    },
    {
      "type": "ol",
      "items": [
        "Open each product and read its Title and Description as if you were a customer typing a search.",
        "Add the plain words customers would use, including alternate names.",
        "Check that every size is in the Sizes field with its stock.",
        "Test from a second Instagram account by typing a few real questions and seeing what comes back."
      ]
    }
  ],
  "related": [
    {
      "to": "/catalog-guide",
      "title": "Catalog Guide",
      "description": "How search, sizes, stock and categories work in full."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "See the search limits for each plan."
    },
    {
      "to": "/help",
      "title": "Help Guide",
      "description": "How the pieces of Checkout DM fit together."
    }
  ],
  "faqs": [
    {
      "q": "What does the bot search when a customer types a question?",
      "a": "Your product Title and Description, combined and matched without regard to capital letters. It does not search photos or other fields."
    },
    {
      "q": "Why did a size search return nothing?",
      "a": "A size only matches if it is entered in the product\u2019s Sizes field. Writing sizes only in the description does nothing for search."
    },
    {
      "q": "Does it understand price ranges?",
      "a": "Yes. Phrases such as under 500, below 1k, above 2000 and 500 to 1000 are understood and applied fully on every plan."
    },
    {
      "q": "Why is a T-shirt missing when I search shirt?",
      "a": "Searching shirt deliberately excludes products that only match because they contain T-Shirt. Tshirt, t-shirt and t shirt are read as the same word and match your tees."
    },
    {
      "q": "Do all plans combine the same number of search words?",
      "a": "No. Free combines 1 thing from a message, Starter 2, Growth 3 and Pro 4, counting keyword words plus a size. Price ranges are never limited."
    }
  ]
};

export default post;
